/**
 * Leviathan Web - Receipt Detail Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 */

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { API_URL } from '@/config/api';

interface ReceiptItem {
  id: string;
  orderedQuantity: number | null;
  receivedQuantity: number;
  unit: string;
  unitCost: number;
  lineTotal: number;
  notes: string | null;
  product: {
    id: string;
    name: string;
    sku: string;
    unit: string;
  };
  tank?: {
    id: string;
    tankNumber: string;
    name: string;
  };
}

interface Receipt {
  id: string;
  receiptNumber: string;
  receiptDate: string;
  deliveryDate: string | null;
  status: string;
  supplierInvoice: string | null;
  bolNumber: string | null;
  poNumber: string | null;
  deliveredBy: string | null;
  carrierName: string | null;
  vehicleNumber: string | null;
  subtotal: number;
  taxAmount: number;
  freightCharge: number;
  otherCharges: number;
  total: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  supplier: {
    id: string;
    name: string;
    supplierNumber: string;
    phone: string | null;
    email: string | null;
  };
  items: ReceiptItem[];
}

const VALID_STATUS_TRANSITIONS: Record<string, string[]> = {
  PENDING: ['RECEIVED', 'CANCELLED'],
  RECEIVED: ['VERIFIED', 'DISPUTED', 'CANCELLED'],
  VERIFIED: ['POSTED'],
  POSTED: [],
  DISPUTED: ['VERIFIED', 'CANCELLED'],
  CANCELLED: [],
};

const ReceiptDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchReceipt();
  }, [id]);

  const fetchReceipt = async () => {
    try {
      const response = await fetch(`${API_URL}/api/receipts/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        const data = await response.json();
        setReceipt(data);
      } else {
        setError('Receipt not found');
      }
    } catch (error) {
      console.error('Failed to fetch receipt:', error);
      setError('Failed to load receipt');
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatus = async (newStatus: string) => {
    if (!receipt) return;
    setIsUpdating(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/receipts/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        const data = await response.json();
        setReceipt(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update status');
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      setError('Failed to update status');
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteReceipt = async () => {
    if (!receipt || receipt.status !== 'PENDING') return;
    if (!confirm('Are you sure you want to delete this receipt?')) return;

    try {
      const response = await fetch(`${API_URL}/api/receipts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        navigate('/receipts');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete receipt');
      }
    } catch (error) {
      console.error('Failed to delete receipt:', error);
      setError('Failed to delete receipt');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return `$${Number(amount).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'PENDING':
        return 'text-warning-amber bg-warning-amber/20 border-warning-amber/30';
      case 'RECEIVED':
        return 'text-steel bg-steel/20 border-steel/30';
      case 'VERIFIED':
        return 'text-success-green bg-success-green/20 border-success-green/30';
      case 'POSTED':
        return 'text-success-green bg-success-green/20 border-success-green/30';
      case 'DISPUTED':
        return 'text-error-red bg-error-red/20 border-error-red/30';
      case 'CANCELLED':
        return 'text-text-tertiary bg-text-tertiary/20 border-text-tertiary/30';
      default:
        return 'text-text-secondary bg-text-secondary/20 border-text-secondary/30';
    }
  };

  const getStatusButtonColor = (status: string): string => {
    switch (status) {
      case 'RECEIVED':
        return 'bg-steel/20 hover:bg-steel/30 text-steel border-steel/30';
      case 'VERIFIED':
        return 'bg-success-green/20 hover:bg-success-green/30 text-success-green border-success-green/30';
      case 'POSTED':
        return 'bg-amber/20 hover:bg-amber/30 text-amber border-amber/30';
      case 'DISPUTED':
        return 'bg-error-red/20 hover:bg-error-red/30 text-error-red border-error-red/30';
      case 'CANCELLED':
        return 'bg-text-tertiary/20 hover:bg-text-tertiary/30 text-text-tertiary border-text-tertiary/30';
      default:
        return 'bg-panel-border hover:bg-steel/20 text-text-primary border-panel-border';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-text-tertiary font-mono animate-pulse">&gt; Loading receipt...</div>
      </div>
    );
  }

  if (error && !receipt) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96">
        <div className="text-error-red font-mono mb-4">&gt; {error}</div>
        <Link to="/receipts" className="text-steel hover:text-amber font-mono transition-colors">
          &larr; Back to Receipts
        </Link>
      </div>
    );
  }

  if (!receipt) return null;

  const validTransitions = VALID_STATUS_TRANSITIONS[receipt.status] || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-4">
                <h1
                  className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                  style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
                >
                  {receipt.receiptNumber}
                </h1>
                <span
                  className={`px-3 py-1 font-mono text-sm uppercase tracking-wider border ${getStatusColor(receipt.status)}`}
                >
                  {receipt.status}
                </span>
              </div>
              <div className="flex items-center space-x-4 mt-2">
                <Link
                  to="/receipts"
                  className="text-text-secondary text-sm font-mono hover:text-amber transition-colors"
                >
                  &larr; Back to Receipts
                </Link>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {receipt.status === 'PENDING' && (
                <button
                  onClick={deleteReceipt}
                  className="px-4 py-2 bg-error-red/20 hover:bg-error-red/30 text-error-red font-mono text-sm uppercase tracking-wider transition-all border border-error-red/30"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-error-red/10 border border-error-red/50">
          <p className="text-error-red text-sm font-mono">&gt; Error: {error}</p>
        </div>
      )}

      {/* Status Actions */}
      {validTransitions.length > 0 && (
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border p-4 clip-path-octagon">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
              <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                Status Actions
              </h3>
            </div>
            <div className="flex flex-wrap gap-3">
              {validTransitions.map((status) => (
                <button
                  key={status}
                  onClick={() => updateStatus(status)}
                  disabled={isUpdating}
                  className={`px-6 py-2 font-mono text-sm uppercase tracking-wider transition-all border disabled:opacity-50 ${getStatusButtonColor(status)}`}
                >
                  {isUpdating ? 'Updating...' : `Mark as ${status}`}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Receipt Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Supplier Info */}
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
              <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                Supplier
              </h3>
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-text-primary font-mono font-semibold text-lg">
                  {receipt.supplier.name}
                </div>
                <div className="text-text-tertiary font-mono text-sm">
                  {receipt.supplier.supplierNumber}
                </div>
              </div>
              {receipt.supplier.phone && (
                <div className="text-text-secondary font-mono text-sm">
                  Phone: {receipt.supplier.phone}
                </div>
              )}
              {receipt.supplier.email && (
                <div className="text-text-secondary font-mono text-sm">
                  Email: {receipt.supplier.email}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Receipt Info */}
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
              <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                Receipt Details
              </h3>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-text-tertiary font-mono uppercase text-xs">Receipt Date</div>
                <div className="text-text-primary font-mono">{formatDate(receipt.receiptDate)}</div>
              </div>
              {receipt.deliveryDate && (
                <div>
                  <div className="text-text-tertiary font-mono uppercase text-xs">
                    Delivery Date
                  </div>
                  <div className="text-text-primary font-mono">
                    {formatDate(receipt.deliveryDate)}
                  </div>
                </div>
              )}
              {receipt.supplierInvoice && (
                <div>
                  <div className="text-text-tertiary font-mono uppercase text-xs">Invoice #</div>
                  <div className="text-text-primary font-mono">{receipt.supplierInvoice}</div>
                </div>
              )}
              {receipt.bolNumber && (
                <div>
                  <div className="text-text-tertiary font-mono uppercase text-xs">BOL #</div>
                  <div className="text-text-primary font-mono">{receipt.bolNumber}</div>
                </div>
              )}
              {receipt.poNumber && (
                <div>
                  <div className="text-text-tertiary font-mono uppercase text-xs">PO #</div>
                  <div className="text-text-primary font-mono">{receipt.poNumber}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delivery Info */}
      {(receipt.deliveredBy || receipt.carrierName || receipt.vehicleNumber) && (
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
              <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                Delivery Information
              </h3>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              {receipt.deliveredBy && (
                <div>
                  <div className="text-text-tertiary font-mono uppercase text-xs">Delivered By</div>
                  <div className="text-text-primary font-mono">{receipt.deliveredBy}</div>
                </div>
              )}
              {receipt.carrierName && (
                <div>
                  <div className="text-text-tertiary font-mono uppercase text-xs">Carrier</div>
                  <div className="text-text-primary font-mono">{receipt.carrierName}</div>
                </div>
              )}
              {receipt.vehicleNumber && (
                <div>
                  <div className="text-text-tertiary font-mono uppercase text-xs">Vehicle #</div>
                  <div className="text-text-primary font-mono">{receipt.vehicleNumber}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Line Items */}
      <div className="relative stat-card">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border clip-path-octagon overflow-hidden">
          <div className="p-6 border-b border-panel-border">
            <div className="flex items-center space-x-2">
              <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
              <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                Line Items
              </h3>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-background border-b border-panel-border">
                <tr>
                  <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Product
                  </th>
                  <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Tank
                  </th>
                  <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Ordered
                  </th>
                  <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Received
                  </th>
                  <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Unit Cost
                  </th>
                  <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Line Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {receipt.items.map((item) => (
                  <tr key={item.id} className="border-b border-panel-border">
                    <td className="px-6 py-4">
                      <div className="text-text-primary font-mono font-semibold">
                        {item.product.name}
                      </div>
                      <div className="text-text-tertiary font-mono text-xs">{item.product.sku}</div>
                    </td>
                    <td className="px-6 py-4 text-text-secondary font-mono text-sm">
                      {item.tank ? `${item.tank.tankNumber} - ${item.tank.name}` : '-'}
                    </td>
                    <td className="px-6 py-4 text-right text-text-secondary font-mono">
                      {item.orderedQuantity
                        ? `${item.orderedQuantity.toLocaleString()} ${item.unit}`
                        : '-'}
                    </td>
                    <td className="px-6 py-4 text-right text-text-primary font-mono font-semibold">
                      {item.receivedQuantity.toLocaleString()} {item.unit}
                    </td>
                    <td className="px-6 py-4 text-right text-text-secondary font-mono">
                      {formatCurrency(item.unitCost)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span
                        className="text-amber font-mono font-semibold"
                        style={{ textShadow: '0 0 6px rgba(255, 149, 0, 0.4)' }}
                      >
                        {formatCurrency(item.lineTotal)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Totals */}
      <div className="relative stat-card">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
          <div className="flex items-center space-x-2 mb-4">
            <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
            <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
              Totals
            </h3>
          </div>
          <div className="max-w-xs ml-auto space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-text-secondary font-mono">Subtotal:</span>
              <span className="text-text-primary font-mono">{formatCurrency(receipt.subtotal)}</span>
            </div>
            {receipt.taxAmount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary font-mono">Tax:</span>
                <span className="text-text-primary font-mono">
                  {formatCurrency(receipt.taxAmount)}
                </span>
              </div>
            )}
            {receipt.freightCharge > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary font-mono">Freight:</span>
                <span className="text-text-primary font-mono">
                  {formatCurrency(receipt.freightCharge)}
                </span>
              </div>
            )}
            {receipt.otherCharges > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary font-mono">Other:</span>
                <span className="text-text-primary font-mono">
                  {formatCurrency(receipt.otherCharges)}
                </span>
              </div>
            )}
            <div className="border-t border-panel-border pt-2 mt-2">
              <div className="flex justify-between">
                <span className="text-text-secondary font-mono font-semibold">Total:</span>
                <span
                  className="text-2xl text-amber font-mono font-bold"
                  style={{ textShadow: '0 0 10px rgba(255, 149, 0, 0.6)' }}
                >
                  {formatCurrency(receipt.total)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}
      {receipt.notes && (
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
              <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
                Notes
              </h3>
            </div>
            <p className="text-text-secondary font-mono text-sm whitespace-pre-wrap">
              {receipt.notes}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceiptDetailPage;
